import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef, Renderer2 } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, FormGroupDirective, Validators } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TagLists } from 'app/models/cx-interaction/cx-interaction-tag-list.interface';
import { environment } from 'environments/environment';
import { Store } from '@ngxs/store';
import { CXInteractionState } from 'app/store/cx-interaction/cx-interaction.state';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Alert } from 'app/models/dto/alert.model';
import { BorrowerService } from 'app/services/borrower/borrower.service';

@Component({
    selector: 'app-cx-interaction-form',
    templateUrl: 'cx-interaction-form.component.html',
    styleUrls: ['cx-interaction-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CxInteractionFormComponent implements OnChanges, OnInit, OnDestroy {

    alerts$: Observable<Alert[]> = this.store.select(CXInteractionState.alerts);

    @Output() saveCxInteraction = new EventEmitter<any>();
    @Output() cxDrawerClose = new EventEmitter<boolean>();
    @Input() tagOptions: Array<any>;

    @Input() formSaving: boolean = false;
    @Input() forcedPrefill: { borrowerId: number | null, interactionType: string | null } | null = null;
    @Input() livePrefill: { borrowerId: number | null, interactionType: string | null } | null = null;
    @Input() locked = false;

    private pendingForcedType: string | null = null;

    tagsDisabled: boolean = true;
    tagsRequired: boolean = true;
    prefilledFields: { interactionType: boolean, borrowerId: boolean } = { interactionType: false, borrowerId: false };
    model: any = {};
    processing = false;

    borrowerIdEditing = false;
    borrowerIdValidating = false;
    borrowerIdError: string | null = null;
    private originalBorrowerId: number | null = null;
    private borrowerLookupSub: Subscription | null = null;

    initialSentimentOptions: Array<object>;
    interactionTypeOptions: Array<object>;
    generalInquiryOptions: Array<object>;
    troubleshootingOptions: Array<object>;
    departmentRequiredOptions: Array<object>;
    otherOptions: Array<object>;
    complaintOptions: Array<object>;
    funnelOptions: Array<object>;
    outcomeOptions: Array<object>;
    appStatusOptions: Array<object>;
    tagLists: TagLists[];
    saveTagValues: Array<any> = [];
    options: FormlyFormOptions = {};
    cxInteractionInfo: object = {};
    fields: FormlyFieldConfig[] = [];
    cxInteractionForm: UntypedFormGroup;

    constructor(
        private fb: UntypedFormBuilder,
        private renderer: Renderer2,
        private store: Store,
        private cdr: ChangeDetectorRef,
        private borrowerService: BorrowerService,
    ) {

        this.initialSentimentOptions = [
          {value: 'Positive', label: 'Positive'},
          {value: 'Neutral', label: 'Neutral'},
          {value: 'Negative', label: 'Negative'},
          {value: 'Alerted TL', label: 'Alerted TL'},
        ];

        this.interactionTypeOptions = [
          {value: 'CX Inbound Call', label: 'Inbound Call'},
          {value: 'CX Outbound Call', label: 'Outbound Call'},
          {value: 'CX Chat', label: 'Chats'},
          {value: 'CX Email', label: 'Email'},
          {value: 'CX Social Media', label: 'Social Media'},
        ];

        this.funnelOptions = [
          {
            value: 'No account created',
            label: 'No Account Created'
          },
          {
            value: 'Incomplete Lendio package - Alias Assigned',
            label: 'Incomplete Lendio package - Alias Assigned'
          },
          {
            value: 'Incomplete Lendio Package - ADR Assigned',
            label: 'Incomplete Lendio Package - ADR Assigned'
          },
          {
            value: 'Sam/Emily Jones',
            label: 'Sam/Emily Jones'
          },
          {
            value: 'Assigned Does Not Qualify',
            label: 'Assigned Does Not Qualify'
          },
          {
            value: 'Offer Presentation - Self Serve',
            label: 'Offer Presentation - Self Serve'
          },
          {
            value: 'Offer Presentation - FE Assigned',
            label: 'Offer Presentation - FE Assigned'
          },
          {
            value: 'Received Funds',
            label: 'Received Funds'
          },
          {
            value: 'Renewals Incomplete - BAT Assigned',
            label: 'Renewals Incomplete - BAT Assigned'
          },
          {
            value: 'Renewals Incomplete - ADR Assigned',
            label: 'Renewals Incomplete - ADR Assigned'
          },
          {
            value: 'Renewals Sam/Emily Jones',
            label: 'Renewals Sam/Emily Jones'
          },
          {
            value: 'Renewals Assigned Does Not Qualify',
            label: 'Renewals Assigned Does Not Qualify'
          },
          {
            value: 'Renewals Offer Presentation - Self Serve',
            label: 'Renewals Offer Presentation - Self Serve'
          },
          {
            value: 'Renewals Offer Presentation - FE Assigned',
            label: 'Renewals Offer Presentation - FE Assigned'
          },
        ];

        this.outcomeOptions = [
          {value: 'Issue Resolved', label: 'Issue Resolved'},
          {value: 'Unresolved Issue', label: 'Unresolved Issue'},
          {value: 'Resolved Troubleshooting Issue', label: 'Resolved Troubleshooting Issue'},
          {value: 'Unresolved/Reported Troubleshooting Issue', label: 'Unresolved/Reported Troubleshooting Issue'},
          {value: 'Workaround for Troubleshooting Issue', label: 'Workaround for Troubleshooting Issue'},
          {value: 'Spam', label: 'Spam'},
          {value: 'No Contact', label: 'No Contact'},
        ];

        this.appStatusOptions = [
          {value: 'Completed application', label: 'Completed application'},
          {value: 'Completed application with bank statements', label: 'Completed application with bank statements'},
          {value: 'Not applicable', label: 'Not applicable'},
        ];
    }

    ngOnInit(): void {
      this.alerts$.subscribe((alert) => {
        if (!alert.length) {
          return;
        }
        this.scrollToTop();
      });
      this.generateFormlyFields();
    }

    generateFormlyFields() {
      this.fields = [
        {
          fieldGroupClassName: 'tw-block',
          fieldGroup: [
            {
              fieldGroupClassName: 'tw-my-2',
              fieldGroup: [
                {
                  key: 'borrower_id_label',
                  type: 'padded-label',
                  className: 'tw-pb-2 tw-flex-1 tw-font-normal tw-text-slate-400',
                  templateOptions: {
                      label: 'All fields required',
                  },
                },
              ]
            },
            {
              key: 'borrower_id',
              className: 'loans-field',
              type: 'unseparated-number',
              templateOptions: {
                label: 'BID #',
                hideRequiredMarker: true,
                placeholder: 'Borrower ID Number',
                floatLabel: 'always',
                appearance: 'outline',
                required: false,
                disabled: this.prefilledFields.borrowerId && !this.borrowerIdEditing,
                keydown: this.onBorrowerIdKeydown.bind(this)
              }
            },
            {
              key: 'interaction_type',
              type: 'select',
              className: 'mat-select-value-override',
              templateOptions: {
                appearance: 'outline',
                label: 'Interaction Type',
                placeholder: 'Select Interaction Type',
                hideRequiredMarker: true,
                floatLabel: 'always',
                required: true,
                multiple: false,
                options: this.interactionTypeOptions,
                disabled: this.prefilledFields.interactionType,
                change: this.onInteractionTypeChange.bind(this),
              },
            },
          ]
        },
        {
          fieldGroupClassName: 'tw-block tw-mt-2',
          fieldGroup: [
            {
              fieldGroupClassName: 'tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-gap-y-1 tw-mt-3 tw-mb-4',
              fieldGroup: [
                {
                  key: 'beginning_label',
                  type: 'padded-label',
                  className: 'tw-font-semibold',
                  templateOptions: {
                    label: 'At the beginning of the interaction',
                  },
                },
                {
                  key: 'beginning_label_required',
                  type: 'padded-label',
                  className: 'tw-font-normal tw-text-slate-400',
                  templateOptions: {
                    label: 'All fields required',
                  },
                },
              ]
            },
            {
              key: 'initial_sentiment',
              type: 'select',
              className: 'mat-select-value-override',
              templateOptions: {
                appearance: 'outline',
                label: 'Initial Borrower Sentiment',
                placeholder: 'What was the borrower\'s initial sentiment?',
                hideRequiredMarker: true,
                floatLabel: 'always',
                required: true,
                multiple: false,
                options: this.initialSentimentOptions,
              },
            },
            {
              key: 'funnel',
              type: 'select',
              className: 'mat-select-value-override tw-max-w-fit',
              templateOptions: {
                appearance: 'outline',
                label: 'Part of the Funnel',
                placeholder: 'What part of the funnel was the borrower in?',
                hideRequiredMarker: true,
                floatLabel: 'always',
                required: true,
                options: this.funnelOptions,
              },
            },
          ]
        },
        {
          fieldGroupClassName: 'tw-block tw-mt-2',
          fieldGroup: [
            {
              fieldGroupClassName: 'tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-gap-y-1 tw-mt-3 tw-mb-4',
              fieldGroup: [
                {
                  key: 'conclusion_label',
                  type: 'padded-label',
                  className: 'tw-font-semibold',
                  templateOptions: {
                    label: 'At the conclusion of the interaction',
                  },
                },
                {
                  key: 'conclusion_label_required',
                  type: 'padded-label',
                  className: 'tw-font-normal tw-text-slate-400',
                  templateOptions: {
                    label: 'All fields required',
                  },
                },
              ]
            },
            {
              key: 'outcome',
              type: 'select',

              className: 'mat-select-value-override',
              templateOptions: {
                appearance: 'outline',
                label: 'Outcome',
                placeholder: 'How or was the borrower\'s needs met?',
                hideRequiredMarker: true,
                floatLabel: 'always',
                required: true,
                options: this.outcomeOptions,
              },
            },
            {
              key: 'appStatus',
              type: 'select',

              className: 'mat-select-value-override',
              templateOptions: {
                appearance: 'outline',
                label: 'Assist with Application',
                placeholder: 'Did you assist the borrower with their application?',
                hideRequiredMarker: true,
                floatLabel: 'always',
                required: true,
                options: this.appStatusOptions,
              },
            },
            {
              key: 'note',
              type: 'textarea-auto-resize',
              templateOptions: {
                appearance: 'outline',
                label: 'Note',
                placeholder: 'Leave a detailed note',
                floatLabel: 'always',
                required: true,
                hideRequiredMarker: true,
              },
            },
          ]
        },
        {
          fieldGroupClassName: 'tw-block tw-mt-2',
          fieldGroup: [
            {
              fieldGroupClassName: 'tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-gap-y-1 tw-mt-3 tw-mb-4',
                fieldGroup: [
                  {
                    key: 'purpose_label',
                    type: 'padded-label',
                    className: 'tw-font-semibold',
                    templateOptions: {
                      label: 'Interaction Tags',
                    },
                  },
                  {
                    key: 'purpose_label',
                    type: 'padded-label',
                    className: 'tw-font-normal tw-text-slate-400',
                    templateOptions: {
                      label: 'Select all that apply',
                    },
                  },
                ]
              },
              {
                key: 'general_inquiry',
                type: 'select',

                className: 'mat-select-value-override',
                templateOptions: {
                  appearance: 'outline',
                  label: 'General Inquiries',
                  placeholder: 'General Inquiries categories',
                  hideRequiredMarker: true,
                  floatLabel: 'always',
                  required: this.tagsRequired,
                  multiple: true,
                  hideExpression: true,
                  options: this.generalInquiryOptions,
                  disabled: this.tagsDisabled,
                  change: this.onTagChange.bind(this),
                },
              },
              {
                  key: 'trouble_shooting',
                  type: 'select',

                  className: 'mat-select-value-override',
                  templateOptions: {
                    appearance: 'outline',
                    label: 'Troubleshooting',
                    placeholder: 'Troubleshooting Issues categories',
                    hideRequiredMarker: true,
                    floatLabel: 'always',
                    required: this.tagsRequired,
                    multiple: true,
                    options: this.troubleshootingOptions,
                    disabled: this.tagsDisabled,
                    change: this.onTagChange.bind(this)
                  },
              },
              {
                  key: 'department_required',
                  type: 'select',

                  className: 'mat-select-value-override',
                  templateOptions: {
                    appearance: 'outline',
                    label: 'Requires Another Dept',
                    placeholder: 'Requires Another Dept. categories',
                    hideRequiredMarker: true,
                    floatLabel: 'always',
                    required: this.tagsRequired,
                    multiple: true,
                    options: this.departmentRequiredOptions,
                    disabled: this.tagsDisabled,
                    change: this.onTagChange.bind(this)
                  },
              },
              {
                  key: 'other',
                  type: 'select',

                  className: 'mat-select-value-override',
                  templateOptions: {
                    appearance: 'outline',
                    label: 'Other',
                    placeholder: 'Other categories',
                    hideRequiredMarker: true,
                    floatLabel: 'always',
                    required: this.tagsRequired,
                    multiple: true,
                    options: this.otherOptions,
                    disabled: this.tagsDisabled,
                    change: this.onTagChange.bind(this)
                  },
              },
              {
                  key: 'complaints',
                  type: 'select',

                  className: 'mat-select-value-override',
                  templateOptions: {
                    appearance: 'outline',
                    label: 'Complaints',
                    placeholder: 'Complaint categories',
                    hideRequiredMarker: true,
                    floatLabel: 'always',
                    required: this.tagsRequired,
                    multiple: true,
                    options: this.complaintOptions,
                    disabled: this.tagsDisabled,
                    change: this.onTagChange.bind(this)
                  },
              },
          ]
        },
      ];
    }

    ngOnChanges(changes: SimpleChanges) {
        this.cxInteractionForm = this.fb.group({

        });
        if (changes['forcedPrefill']?.currentValue) {
            this.prefillFromCall(changes['forcedPrefill'].currentValue);
        }
        if (changes['livePrefill']?.currentValue && this.isModelEmpty()) {
            this.prefillFromCall(changes['livePrefill'].currentValue, false);
        }
        if (changes['tagOptions']?.currentValue && this.pendingForcedType) {
            const typeKey = this.pendingForcedType.replace(/ /g, '_');
            if ((this.tagOptions as any)?.[typeKey]) {
                this.onInteractionTypeChange(null, this.pendingForcedType);
                this.pendingForcedType = null;
                this.cdr.markForCheck();
            }
        }
    }

    private isModelEmpty(): boolean {
        return Object.values(this.model ?? {}).every((value) => {
            if (Array.isArray(value)) {
                return value.length === 0;
            }
            return value === null || value === undefined || value === '';
        });
    }

    private prefillFromCall(prefill: { borrowerId: number | null, interactionType: string | null }, lockFields = true) {
        if (lockFields) {
            this.prefilledFields = {
                interactionType: !!prefill.interactionType,
                borrowerId: !!prefill.borrowerId,
            };
            this.resetBorrowerIdEdit();
        }
        this.model = {
            ...this.model,
            interaction_type: prefill.interactionType,
            borrower_id: prefill.borrowerId ?? null,
        };
        const interactionType = prefill.interactionType;
        const typeKey = interactionType?.replace(/ /g, '_');
        if (interactionType && typeKey && (this.tagOptions as any)?.[typeKey]) {
            this.pendingForcedType = null;
            this.onInteractionTypeChange(null, interactionType);
        } else {
            this.pendingForcedType = typeKey ? interactionType : null;
            this.generateFormlyFields();
        }
        this.cdr.markForCheck();
    }


    onSaveCxInteraction(formDirective: FormGroupDirective) {
      if (!this.cxInteractionForm.valid || this.borrowerIdEditing || this.borrowerIdValidating) {
        return;
      }
      this.processing = true;
      this.saveTagValues = [];
      const formValue = this.cxInteractionForm.getRawValue();
      this.setTagValue(formValue.general_inquiry);
      this.setTagValue(formValue.trouble_shooting);
      this.setTagValue(formValue.department_required);
      this.setTagValue(formValue.other);
      this.setTagValue(formValue.complaints);
      const rawBorrowerId = formValue.borrower_id;
      this.setCxInteractionObject('borrowerId', rawBorrowerId === '' || rawBorrowerId == null ? null : rawBorrowerId);
      this.setCxInteractionObject('interactionType', this.removeCxString(formValue.interaction_type));
      this.setCxInteractionObject('sentiment', formValue.initial_sentiment);
      this.setCxInteractionObject('note', formValue.note);
      this.setCxInteractionObject('funnelStatus', formValue.funnel);
      this.setCxInteractionObject('interactionOutcome', formValue.outcome);
      this.setCxInteractionObject('appStatus', formValue.appStatus);
      this.setCxInteractionObject('tags', this.saveTagValues);
      this.saveCxInteraction.emit({
        'info': this.cxInteractionInfo,
        'form': this.cxInteractionForm,
        'directive': formDirective,
        'cxComponent': this
      });
    }

    setCxInteractionObject(key, value) {
      this.cxInteractionInfo[key] = value;
    }

    setTagValue(tagValues){
      if (tagValues) {
        tagValues.map((value) => {
          this.saveTagValues.push(value);
        });
      }
    }

    removeCxString(string) {
      const indexOfSpace = string.indexOf(' ');
      return string.substring(indexOfSpace + 1);
    }

    ngOnDestroy(): void {
      this.borrowerLookupSub?.unsubscribe();
    }

    private resetBorrowerIdEdit() {
      // Drop any in-flight lookup so a late result can't restore a stale BID after a reset.
      this.borrowerLookupSub?.unsubscribe();
      this.borrowerLookupSub = null;
      this.borrowerIdEditing = false;
      this.borrowerIdValidating = false;
      this.borrowerIdError = null;
      this.originalBorrowerId = null;
    }

    private get borrowerIdControl() {
      return this.cxInteractionForm?.get('borrower_id');
    }

    private setBorrowerIdValue(value: number | null) {
      this.model = { ...this.model, borrower_id: value };
      this.borrowerIdControl?.setValue(value);
    }

    /** Unlocks the pre-filled BID so the rep can type a corrected borrower ID. */
    startBorrowerIdEdit() {
      if (!this.prefilledFields.borrowerId || this.borrowerIdEditing) {
        return;
      }
      this.originalBorrowerId = this.model.borrower_id ?? null;
      this.borrowerIdError = null;
      this.borrowerIdEditing = true;
      this.generateFormlyFields();
      this.cdr.markForCheck();
    }

    /** Enter in the BID input applies the edit instead of submitting the form. */
    onBorrowerIdKeydown(field?: any, event?: KeyboardEvent) {
      if (event?.key !== 'Enter' || !this.borrowerIdEditing) {
        return;
      }
      event.preventDefault();
      this.applyBorrowerIdEdit();
    }

    /** Puts the original BID back and re-locks the field. */
    cancelBorrowerIdEdit() {
      if (!this.borrowerIdEditing || this.borrowerIdValidating) {
        return;
      }
      this.setBorrowerIdValue(this.originalBorrowerId);
      this.lockBorrowerId();
    }

    /**
     * Validates the typed BID against the API. A valid, different borrower replaces the
     * original; anything else restores the original. Either way the field re-locks, so the
     * rep can click edit again to retry.
     */
    applyBorrowerIdEdit() {
      if (!this.borrowerIdEditing || this.borrowerIdValidating) {
        return;
      }
      const raw = this.borrowerIdControl?.value;
      const newId = Number(raw);
      if (raw === '' || raw == null || !Number.isInteger(newId) || newId <= 0) {
        this.failBorrowerIdEdit('Enter a valid borrower ID.');
        return;
      }
      if (newId === this.originalBorrowerId) {
        this.lockBorrowerId();
        return;
      }
      this.borrowerIdValidating = true;
      this.borrowerIdError = null;
      this.cdr.markForCheck();
      let responded = false;
      this.borrowerLookupSub = this.borrowerService.get(newId).pipe(take(1)).subscribe({
        next: (borrower) => {
          responded = true;
          if (!borrower) {
            this.failBorrowerIdEdit(`Borrower #${newId} was not found.`);
            return;
          }
          this.borrowerIdValidating = false;
          this.setBorrowerIdValue(newId);
          this.lockBorrowerId();
        },
        error: (err) => {
          responded = true;
          this.failBorrowerIdEdit(err?.status === 404
            ? `Borrower #${newId} was not found.`
            : `Could not verify borrower #${newId}. Please try again.`);
        },
        // The API interceptor turns network failures into an empty completion (no next/error).
        complete: () => {
          if (!responded) {
            this.failBorrowerIdEdit(`Could not verify borrower #${newId}. Please try again.`);
          }
        },
      });
    }

    private failBorrowerIdEdit(message: string) {
      this.borrowerIdValidating = false;
      this.setBorrowerIdValue(this.originalBorrowerId);
      this.lockBorrowerId();
      this.borrowerIdError = `${message} Kept borrower #${this.originalBorrowerId}.`;
      this.cdr.markForCheck();
    }

    private lockBorrowerId() {
      this.borrowerIdEditing = false;
      this.generateFormlyFields();
      this.cdr.markForCheck();
    }

    onTagChange(event) {
      const generalValue = this.cxInteractionForm.value.general_inquiry ?? [];
      const troubleValue = this.cxInteractionForm.value.trouble_shooting ?? [];
      const departmentValue = this.cxInteractionForm.value.department_required ?? [];
      const otherValue = this.cxInteractionForm.value.other ?? [];
      const complaintValue = this.cxInteractionForm.value.complaints ?? [];

      const valueLength = generalValue.length
        + troubleValue.length
        + departmentValue.length
        + otherValue.length
        + complaintValue.length;

      if (valueLength < 1) {
        this.tagsRequired = true;
        this.cxInteractionForm.controls.general_inquiry.setValidators([Validators.required]);
        this.cxInteractionForm.controls.general_inquiry.updateValueAndValidity();
        this.cxInteractionForm.controls.trouble_shooting.setValidators([Validators.required]);
        this.cxInteractionForm.controls.trouble_shooting.updateValueAndValidity();
        this.cxInteractionForm.controls.department_required.setValidators([Validators.required]);
        this.cxInteractionForm.controls.department_required.updateValueAndValidity();
        this.cxInteractionForm.controls.other.setValidators([Validators.required]);
        this.cxInteractionForm.controls.other.updateValueAndValidity();
        this.cxInteractionForm.controls.complaints.setValidators([Validators.required]);
        this.cxInteractionForm.controls.complaints.updateValueAndValidity();
      } else {
        this.tagsRequired = false;
        this.cxInteractionForm.controls.general_inquiry.clearValidators();
        this.cxInteractionForm.controls.general_inquiry.updateValueAndValidity();
        this.cxInteractionForm.controls.trouble_shooting.clearValidators();
        this.cxInteractionForm.controls.trouble_shooting.updateValueAndValidity();
        this.cxInteractionForm.controls.department_required.clearValidators();
        this.cxInteractionForm.controls.department_required.updateValueAndValidity();
        this.cxInteractionForm.controls.other.clearValidators();
        this.cxInteractionForm.controls.other.updateValueAndValidity();
        this.cxInteractionForm.controls.complaints.clearValidators();
        this.cxInteractionForm.controls.complaints.updateValueAndValidity();
      }

    }

    resetDefaultFormSettings() {
      this.tagsRequired = true;
      this.tagsDisabled = true;
      this.prefilledFields = { interactionType: false, borrowerId: false };
      this.resetBorrowerIdEdit();
      this.model = {};
      this.cxInteractionInfo = {};
      this.saveTagValues = [];
      this.generateFormlyFields();
      this.processing = false;
    }


    onInteractionTypeChange(event?, interactionType?: string) {
        const explicitType = typeof interactionType === 'string' ? interactionType : undefined;
        const type = (explicitType ?? this.cxInteractionForm.value.interaction_type).replace(/ /g,'_');

        //general inquiry options
        [this.tagOptions].filter((data) => {
          const genOpts = data[type].categories.generalInquiry.tags;
          this.generalInquiryOptions = genOpts.map((tag) => ({
            label: tag['tagName'],
            value: tag['tagId']
           } ));

          //troubleshooting options
          const trouble = data[type].categories.troubleshooting.tags;
          this.troubleshootingOptions = trouble.map((tag) => ({
            label: tag['tagName'],
            value: tag['tagId']
           } ));

          //complaint options
          const complaint = data[type].categories.complaint.tags;
          this.complaintOptions = complaint.map((tag) => ({
            label: tag['tagName'],
            value: tag['tagId']
           } ));

          //other options
          const other = data[type].categories.other.tags;
          this.otherOptions = other.map((tag) => ({
            label: tag['tagName'],
            value: tag['tagId']
           } ));

          //Department required options
          const department = data[type].categories.departmentRequired.tags;
          this.departmentRequiredOptions = department.map((tag) => ({
            label: tag['tagName'],
            value: tag['tagId']
           } ));
        });


        this.tagsDisabled = false;
        this.generateFormlyFields();

    }

    hasClearableContent(): boolean {
      const values = this.cxInteractionForm?.getRawValue() ?? {};
      return Object.entries(values).some(([key, value]) => {
        if (this.isPrefilledField(key)) {
          return false;
        }
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== null && value !== undefined && value !== '';
      });
    }

    private isPrefilledField(key: string): boolean {
      return (key === 'interaction_type' && this.prefilledFields.interactionType)
        || (key === 'borrower_id' && this.prefilledFields.borrowerId);
    }

    clearForm(formDirective: FormGroupDirective) {
      formDirective.resetForm();
      this.cxInteractionForm.reset();
      this.resetDefaultFormSettings();
      if (this.forcedPrefill && (this.forcedPrefill.borrowerId != null || this.forcedPrefill.interactionType != null)) {
        this.prefillFromCall(this.forcedPrefill);
      }
    }

    closeDrawer() {
      this.cxDrawerClose.emit(true);
    }

  scrollToTop() {
    this.renderer.selectRootElement('#cxAlerts').scrollIntoView({ behavior: 'smooth' });
  }
}

